import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button } from "./button.jsx";

export interface SaveBarProps {
  /** Unsaved changes exist — the bar is on screen. */
  open: boolean;
  /** "Unsaved changes", translated by the caller: this package holds no strings of its own. */
  message: string;
  saveLabel: string;
  busy?: boolean;
  disabled?: boolean;
  /**
   * Click handler for a bar outside a form. Omit it and the button is `type="submit"`: the bar
   * then lives inside the page's form and submits it, so Enter and the bar share one path.
   */
  onSave?: () => void;
}

/**
 * The floating "unsaved changes" bar: rises at the bottom centre of the page the moment a form
 * differs from what is stored, and *is* the page's save control — not a button lost at the end
 * of a long form. Its tone bar is the toast's, in the attention tone.
 */
export function SaveBar({ open, message, saveLabel, busy = false, disabled = false, onSave }: SaveBarProps) {
  if (!open) return null;
  return (
    <div className="dt-save-bar" role="status">
      <span className="dt-tone-bar" aria-hidden="true" />
      <span className="dt-save-bar__message">{message}</span>
      <Button type={onSave ? "button" : "submit"} variant="primary" loading={busy} disabled={disabled} {...(onSave ? { onClick: onSave } : {})}>
        {saveLabel}
      </Button>
    </div>
  );
}

function snapshot(form: HTMLFormElement): string {
  return JSON.stringify([...new FormData(form)].map(([key, value]) => [key, typeof value === "string" ? value : `${value.name}:${value.size}`]));
}

/**
 * Whether a form differs from what it held when it mounted. It compares the form's data, so
 * uncontrolled fields count, and so do hidden inputs React adds, changes or removes.
 * Put `ref` on the form and `check` on its `onInput` and `onChange`; remount the form (a new
 * `key`) once a save lands, so what was saved becomes the new baseline.
 */
export function useFormDirty() {
  const ref = useRef<HTMLFormElement>(null);
  const initial = useRef<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const check = useCallback(() => {
    if (ref.current && initial.current !== null) setDirty(snapshot(ref.current) !== initial.current);
  }, []);
  useLayoutEffect(() => {
    if (ref.current) initial.current = snapshot(ref.current);
  }, []);
  // A hidden input changed by state fires no input event, so look again after every render.
  useEffect(check);
  return { ref, dirty, check };
}
